import React, { CSSProperties, ReactElement, ReactNode } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Box, BoxProps } from '@mui/material';
import { LIST_INDENTATION_SIZE } from './list.constants';
import { toNumber } from '@shared/helpers/math.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { mergeSx } from '@client/helpers/mui.helpers';

export interface RowCellProps extends Omit<
  BoxProps,
  'height' | 'children' | 'padding'
> {
  children?: ReactNode;
  align?: 'left' | 'center' | 'right';

  /**
   * How much to indent the cell in steps (integer)
   */
  indentation?: number;

  /**
   * How much to space the children of the cell. Defaults to 2.
   */
  gap?: number;

  /**
   * Default width of the cell in cell. Applied with flex-basis
   */
  width?: number;

  /**
   * How much in relation to the siblings the cell should grow.
   * Defaults to 0
   */
  responsiveWidth?: number;

  /**
   * Floor width in px. Defaults to 0 so shrinking cells can collapse.
   * Pass a value to keep the cell (and its label) clickable.
   */
  minWidth?: number;
  ellipsis?: boolean;
  hideOnPrint?: boolean;
  pl?: number | string;
  paddingLeft?: number | string;
  paddingRight?: CSSProperties['paddingRight'] | Record<string, number>;
  visibility?: CSSProperties['visibility'];
  fontWeight?: CSSProperties['fontWeight'];
}

export const RowCell: React.FC<RowCellProps> = ({
  children,
  align,
  indentation,
  width,
  responsiveWidth,
  minWidth,
  ellipsis = false,
  hideOnPrint = false,
  pl,
  paddingLeft,
  paddingRight,
  visibility,
  fontWeight,
  sx,
  ...props
}: RowCellProps): ReactElement<RowCellProps> => {
  const { classes, cx } = useStyles({ gap: props.gap });
  const justifyContent = alignToJustifyContent(align);
  const defaultGrow = typeof width === 'number' ? 0 : 1;
  const flexGrow: number = responsiveWidth ?? defaultGrow;
  const resolvedPaddingLeft = toNumber(paddingLeft ?? pl);
  const indentationWidth =
    LIST_INDENTATION_SIZE * (indentation ?? 0) + resolvedPaddingLeft;

  const fixedWidth = flexGrow === 0;

  return (
    <Box
      {...props}
      className={cx(classes.cell, props.className)}
      sx={mergeSx(
        {
          flexShrink: fixedWidth ? 0 : 1,
          flexGrow: flexGrow,
          flexBasis: (width ?? 0) + indentationWidth,
          minWidth: minWidth ?? 0,
          justifyContent: justifyContent,
          paddingLeft: indentationWidth + 'px',
          paddingRight,
          visibility,
          fontWeight,
          displayPrint: hideOnPrint ? 'none' : undefined,
        },
        sx,
      )}
    >
      {fixedWidth && ellipsis ? (
        <Box className={classes.ellipsis}>{children}</Box>
      ) : (
        children
      )}
    </Box>
  );
};

const alignToJustifyContent = (align: RowCellProps['align']) => {
  switch (align) {
    case 'right':
      return 'flex-end';
    case 'center':
      return 'center';
    case 'left':
      return 'flex-start';
  }
};

const useStyles = makeStyles<{ gap?: RowCellProps['gap'] }>()(
  ({ spacing }, { gap = 2 }) => ({
    cell: {
      position: 'relative',
      alignItems: 'center',
      display: 'flex',
      gap: guards.is.number(gap) ? spacing(gap) : undefined,
      overflow: 'visible',
    },
    ellipsis: {
      display: 'inline-block',
      width: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      flexShrink: 0,
    },
    hideOnPrint: {
      displayPrint: 'none',
    },
  }),
);
